import { waitUntil } from '@/helpers/waitUntil';
import { store } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import { type ResolvedStartDeviceNames } from '@/tauri/resolveDeviceNamesForStart';
import { createModel } from '@rematch/core';
import { cloneDeep } from 'lodash-es';
import { type RootModel } from '.';
import { type RecordingError } from './tauriRecordErrors';
// import { RecordingStatus } from '../scribeModel';
import { logger } from '@/utils/logger/logger';
// todo use tanstack's states

export enum UploadStatus {
  Queued = 'queued',
  Uploading = 'uploading',
  Completed = 'completed',
  Failed = 'failed',
}
export { RecordingError } from './tauriRecordErrors';

export type CaptureSilenceSource = 'mic' | 'speaker';

/**
 * A pending silent-capture trigger for one source, set by the ipc.ts
 * `AUDIO_CAPTURE_ACTIVITY` listener and consumed (one-shot, like
 * `recordingError`) by `CaptureSilenceRecovery`, which owns all policy.
 */
export type CaptureSilenceEntry = {
  /** Seconds of continuous silence when the monitor fired. */
  silenceSecs: number;
  /** Whether the source ever produced energetic audio this producer lifetime. */
  seenEnergetic: boolean;
  /** Producer-spawn id used for stale-event filtering. */
  captureInstanceId: number;
};

export type CaptureSilenceWarningVariant =
  /** Speaker silent during a matched telehealth call — one-click "Capture {app} audio". */
  | 'speaker_capture_app'
  /** Mic never energetic and a comms-default mic differs from the one in use — "Use {mic}". */
  | 'mic_use_comms_default'
  /**
   * Mic never energetic, no better mic to suggest, and the silent-mic
   * hard stop is armed. Names the in-use mic and states the real stop
   * condition: Heidi keeps recording, and stops on its own only when NO
   * source is capturing audio at all. The copy must not promise a stop on the
   * mic latch alone — the hard stop needs `captureNeverEnergetic` true for
   * BOTH sources. Distinct from `mic_informational` because a source that
   * never produced a sample is a device fault, not a quiet room.
   */
  | 'mic_never_energetic'
  /**
   * Mic silent with no one-click fix to offer: never energetic with no better
   * mic known, or post-speech silence (the mic WAS working, so a mic switch
   * is not the remedy). Informational only.
   */
  | 'mic_informational';

/**
 * A policy-approved warning that is currently visible. Drives the popover and
 * the persistent red device-pill treatment until the source reports `active`
 * again or the recording ends. Distinct from `captureSilence` (the raw
 * one-shot trigger) because most triggers produce no warning at all.
 */
export type CaptureSilenceWarning = {
  source: CaptureSilenceSource;
  variant: CaptureSilenceWarningVariant;
  /**
   * Matched telehealth app name (speaker), suggested mic label (mic
   * actionable), or the in-use mic label (`mic_never_energetic`).
   */
  targetLabel?: string;
};

/**
 * DSK-910: a chunk cadence together with the recording session it was locked
 * for. See {@link InitialState.lockedChunkInterval}.
 */
export type LockedChunkInterval = {
  sessionId: string;
  chunkIntervalSecs: number;
};

export type InitialState = {
  chunks: {
    current_index: number;
    blobs: {
      name: string;
      duration: number;
      size: number;
      chunk: Blob[];
      status: UploadStatus;
    }[];
    backup_file?: Blob | null;
    /** Plaintext MP3 the blob was read from, so a successful retry can delete it. */
    backup_path?: string | null;
    recording_id?: string;
    session_id: string | null;
    backup_uploaded?: boolean;
  };
  /**
   * APP-3779: Whether this recording uses the v2 EKS transcription service
   * for chunk upload. Resolved once at session start from
   * `useIsAppsUseTranscriptionServiceV2DesktopEnabled` and locked here so
   * mid-session flag flips can't corrupt the upload pipeline. The chunk
   * upload mutation reads this value, not the live flag.
   *
   * Lives at the top level (NOT inside `chunks`) because `setChunks` is a
   * replace reducer — putting this field inside `chunks` meant the first
   * per-chunk update (`useMutateUploadChunkRecording.ts`) wiped the locked
   * value, causing every recording to silently fall back to v1 regardless
   * of the flag.
   */
  useTranscriptionServiceV2?: boolean;
  /**
   * APP-6508: whether this recording's chunk uploads are owned by Rust (the
   * `online_chunk_upload` pipeline) rather than the FE fetch-and-upload path.
   * Resolved once at session start from `useIsAppsRustChunkUploadDesktopEnabled`
   * and locked here so `useMutateStopRecording` routes the stop through the
   * matching path regardless of a mid-session flag flip. Lives at the top level
   * (like `useTranscriptionServiceV2`) so `setChunks` doesn't wipe it.
   */
  useRustChunkUpload?: boolean;
  /**
   * DSK-910: the chunk cadence (seconds) this recording's Rust upload session
   * was actually started with, locked here the moment
   * `onlineChunkUploadBridge.startSession` resolves, and STAMPED WITH THE
   * SESSION THAT LOCKED IT. The stop-time truncation heuristic divides
   * wall-clock by this to derive an expected chunk count, so it MUST be the
   * cadence Rust ran *that* recording at — re-reading the live PostHog
   * `desktop-chunk-size` payload at stop diverges whenever the flag changes
   * mid-consult or the payload is momentarily unreadable (it silently defaults
   * to 45s), which flags a healthy consult as truncated and destroys the note.
   *
   * The stamp is what makes it safe to hold in a single global slot. Without
   * an owner the slot is a shared mutable global read after an await: a start
   * for a *different* session can overwrite it while an earlier stop is still
   * finishing (that start is not held off — the stale-recorder guard in
   * `useMutateStartTauriRecording` only consults the in-flight stop inside
   * `if (staleRecorderLiveness.state !== 'Stopped')`, and a stop in its
   * finalization tail has already left the recorder Stopped), and the earlier
   * stop would then judge its own chunk count against someone else's cadence.
   * A reader must therefore compare {@link LockedChunkInterval.sessionId} with
   * its own session and treat a mismatch exactly like `undefined`: no honest
   * divisor, so skip the heuristic rather than guess.
   *
   * `undefined` means "no locked cadence" — cleared on every start (like
   * `useRustChunkUpload`) and never set when the Rust session failed to start.
   */
  lockedChunkInterval?: LockedChunkInterval;
  /**
   * Whether proactive low-disk monitoring was enabled when this logical
   * capture started. Device-change restarts reuse this locked decision rather
   * than re-reading a rollout flag partway through the same session.
   */
  lowDiskMonitorEnabled?: boolean;
  /**
   * The exact device names passed to `start_recording` plus the speaker
   * capture mode they imply, locked at session start (output of
   * `resolveDeviceNamesForStart`). Set on every start so a prior session's
   * value never leaks; lives at the top level (like the flags above) so
   * `setChunks` doesn't wipe it. Read by analytics enrichment and the
   * silent-capture warning layer.
   */
  lockedStartDevices?: ResolvedStartDeviceNames;
  getCurrentRecordingError?: RecordingError;
  recordingError?: RecordingError;
  interval?: ReturnType<typeof setInterval>;
  /** Pending silent-capture triggers, one-shot per source. */
  captureSilence: Partial<Record<CaptureSilenceSource, CaptureSilenceEntry>>;
  /** Policy-approved warnings driving the popover + red pill treatment. */
  captureSilenceWarnings: Partial<Record<CaptureSilenceSource, CaptureSilenceWarning>>;
  /** Sources already warned this recording (analytics/notification cap). */
  captureSilenceWarnedSources: Partial<Record<CaptureSilenceSource, boolean>>;
  /**
   * Per-source `capture_instance_id` high-water marks. The ipc listener
   * discards events below the mark (stale monitor); reset per recording start
   * so a Rust-side counter restart can't strand the FE.
   */
  captureActivityHighWater: Record<CaptureSilenceSource, number>;
  /**
   * Epoch-ms fence set by every `resetCaptureSilence`; the ipc listener
   * discards events whose Rust emission `ts` predates it. Covers what the id
   * high-water can't: a source that never emitted this recording has a stale
   * mark, but old monitors are always torn down before a reset dispatches.
   */
  captureActivityAcceptedAfterTs: number;
  /**
   * Per-source clear generation, bumped by every clear/reset. Lets an async
   * policy cycle detect that its episode was cleared (recovery/stop/restart)
   * while it awaited the device refresh — else that clear would be lost and a
   * warning shown for an already-healthy source.
   */
  captureSilenceClearGen: Record<CaptureSilenceSource, number>;
  /**
   * Per-source "has produced NO energetic audio at all this
   * recording" latch. Set when a `silent` event arrives with
   * `seenEnergetic: false` (the monitor's 20 s never-energetic alert), cleared
   * by the source's `active` recovery, wiped on every reset. Unlike
   * `captureSilence` (one-shot, consumed by policy) this persists so the
   * hard-stop timer and the stop mutation can ask "did this session ever hear
   * anything?" long after the trigger was consumed. A source that never
   * emitted stays `false` — absence of evidence is not treated as silence.
   */
  captureNeverEnergetic: Record<CaptureSilenceSource, boolean>;
};

const initialState: InitialState = {
  chunks: {
    current_index: 0,
    blobs: [],
    session_id: null,
  },
  captureSilence: {},
  captureSilenceWarnings: {},
  captureSilenceWarnedSources: {},
  captureActivityHighWater: { mic: 0, speaker: 0 },
  captureActivityAcceptedAfterTs: 0,
  captureSilenceClearGen: { mic: 0, speaker: 0 },
  captureNeverEnergetic: { mic: false, speaker: false },
};

export const tauriRecordModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setChunks(state, payload: InitialState['chunks']) {
      return { ...state, chunks: payload };
    },
    setInterval(state, payload: InitialState['interval']) {
      return { ...state, interval: payload };
    },
    /**
     * APP-3779: lock the v2 boolean for the lifetime of this recording.
     * Called once at session start by `useMutateStartTauriRecording`; read
     * on every chunk by `useMutateUploadChunkRecording`. Survives the
     * `setChunks` replace because it lives at the top level.
     */
    setUseTranscriptionServiceV2(state, payload: boolean) {
      return { ...state, useTranscriptionServiceV2: payload };
    },
    /**
     * APP-6508: lock whether Rust owns chunk upload for this recording. Set
     * once at session start by `useMutateStartTauriRecording` (both true and
     * false, so a prior session's value never leaks), read at stop by
     * `useMutateStopRecording`.
     */
    setUseRustChunkUpload(state, payload: boolean) {
      return { ...state, useRustChunkUpload: payload };
    },
    /**
     * DSK-910: lock the chunk cadence Rust is actually running for this
     * recording, stamped with the session it belongs to. Called with
     * `undefined` at every session start (both the Rust and legacy branches,
     * so a prior session's cadence never leaks) and with the real value only
     * once `startSession` has resolved — if it throws, no Rust session
     * activated and no cadence was locked. The stamp is not decoration: it is
     * what lets a stop tell its own cadence from one a concurrent start
     * overwrote the slot with.
     */
    setLockedChunkInterval(state, payload?: LockedChunkInterval) {
      return { ...state, lockedChunkInterval: payload };
    },
    /** Lock proactive low-disk monitoring for this logical capture. */
    setLowDiskMonitorEnabled(state, payload: boolean) {
      return { ...state, lowDiskMonitorEnabled: payload };
    },
    /**
     * Lock the resolved start-time device names + speaker capture mode for
     * this recording. Set by both start sites (the start hook and the
     * legacy device-change restart).
     */
    setLockedStartDevices(state, payload: ResolvedStartDeviceNames) {
      return { ...state, lockedStartDevices: payload };
    },
    editBackupUploaded(state, payload: boolean) {
      return { ...state, chunks: { ...state.chunks, backup_uploaded: payload } };
    },
    editChunkStatus(
      state,
      payload: { sessionId: string; index: number; status: UploadStatus }
    ) {
      if (state.chunks.session_id !== payload.sessionId) {
        return state;
      }
      const chunks = cloneDeep(state.chunks);
      const blobEdited = chunks.blobs[payload.index];
      if (blobEdited) {
        blobEdited.status = payload.status;
      }
      return { ...state, chunks };
    },
    setRecordingError(state, payload?: RecordingError) {
      return { ...state, recordingError: payload };
    },

    setCurrentRecordingError(state, payload?: RecordingError) {
      return { ...state, getCurrentRecordingError: payload };
    },
    setBackupFile(state, payload: { blob: Blob; path: string }) {
      return {
        ...state,
        chunks: {
          ...state.chunks,
          backup_file: payload.blob,
          backup_path: payload.path,
        },
      };
    },

    /**
     * Record a `silent` monitor event: set the one-shot trigger and advance
     * the per-source high-water mark so stale torn-down-monitor events drop.
     */
    setCaptureSilence(
      state,
      payload: { source: CaptureSilenceSource; entry: CaptureSilenceEntry }
    ) {
      return {
        ...state,
        captureSilence: { ...state.captureSilence, [payload.source]: payload.entry },
        captureActivityHighWater: {
          ...state.captureActivityHighWater,
          [payload.source]: Math.max(
            state.captureActivityHighWater[payload.source],
            payload.entry.captureInstanceId
          ),
        },
        // A post-speech alert (`seenEnergetic: true`) proves the source DID
        // work, so it can only ever clear the latch, never set it.
        captureNeverEnergetic: {
          ...state.captureNeverEnergetic,
          [payload.source]: !payload.entry.seenEnergetic,
        },
      };
    },

    /**
     * Consume a pending trigger without touching the visible warning —
     * mirrors how `AudioDeviceLostRecovery` consumes `recordingError` so a
     * re-render can't re-fire the policy.
     */
    consumeCaptureSilence(state, payload: CaptureSilenceSource) {
      const remaining = { ...state.captureSilence };
      delete remaining[payload];
      return { ...state, captureSilence: remaining };
    },

    /**
     * An `active` recovery event — drop the pending trigger and the visible
     * warning for the source, and advance the high-water mark, and reset
     * the source's `captureNeverEnergetic` answer.
     */
    clearCaptureSilence(
      state,
      payload: { source: CaptureSilenceSource; captureInstanceId?: number }
    ) {
      const remainingTriggers = { ...state.captureSilence };
      delete remainingTriggers[payload.source];
      const remainingWarnings = { ...state.captureSilenceWarnings };
      delete remainingWarnings[payload.source];
      return {
        ...state,
        captureSilence: remainingTriggers,
        captureSilenceWarnings: remainingWarnings,
        captureActivityHighWater: {
          ...state.captureActivityHighWater,
          [payload.source]: Math.max(
            state.captureActivityHighWater[payload.source],
            payload.captureInstanceId ?? 0
          ),
        },
        captureSilenceClearGen: {
          ...state.captureSilenceClearGen,
          [payload.source]: state.captureSilenceClearGen[payload.source] + 1,
        },
        captureNeverEnergetic: {
          ...state.captureNeverEnergetic,
          [payload.source]: false,
        },
      };
    },

    /** Surface a policy-approved warning (popover + red pill treatment). */
    setCaptureSilenceWarning(state, payload: CaptureSilenceWarning) {
      return {
        ...state,
        captureSilenceWarnings: {
          ...state.captureSilenceWarnings,
          [payload.source]: payload,
        },
      };
    },

    /** Remember this source already warned this recording. */
    markCaptureSilenceWarned(state, payload: CaptureSilenceSource) {
      return {
        ...state,
        captureSilenceWarnedSources: {
          ...state.captureSilenceWarnedSources,
          [payload]: true,
        },
      };
    },

    /**
     * Clear + dismiss on start/stop/restart. Always drops triggers and
     * warnings. A mid-recording restart keeps the once-per-recording warned
     * marks and bumps the high-water marks one past the torn-down producers'
     * ids (the listener drops `id < mark`; respawned producers take strictly
     * greater ids), so a delayed stale event can't resurrect a warning the
     * new producer can never clear. A genuine start/stop wipes everything —
     * a Rust counter restart between recordings must not strand the FE.
     */
    resetCaptureSilence(state, payload?: { isMidRecordingRestart?: boolean }) {
      return {
        ...state,
        captureSilence: {},
        captureSilenceWarnings: {},
        captureSilenceWarnedSources: payload?.isMidRecordingRestart
          ? state.captureSilenceWarnedSources
          : {},
        captureActivityHighWater: payload?.isMidRecordingRestart
          ? {
              mic: state.captureActivityHighWater.mic + 1,
              speaker: state.captureActivityHighWater.speaker + 1,
            }
          : { mic: 0, speaker: 0 },
        // Every reset dispatches after the old monitors are torn down, so
        // rejecting older emission timestamps fences even a source whose
        // instance id the FE never learned (it never emitted this recording).
        captureActivityAcceptedAfterTs: Date.now(),
        captureSilenceClearGen: {
          mic: state.captureSilenceClearGen.mic + 1,
          speaker: state.captureSilenceClearGen.speaker + 1,
        },
        // Wiped on a mid-recording restart too: the respawned monitors run
        // their own never-energetic gate and re-alert at 20 s if the new
        // source is also dead, so a stale latch could only ever mislabel a
        // working replacement device.
        captureNeverEnergetic: { mic: false, speaker: false },
      };
    },

    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    // async startTauriRecording({
    //   // isDemoSession,
    //   session,
    // }: {
    //   isDemoSession?: boolean;
    //   session?: SessionResponse;
    // }) {
    //   try {
    //     const { tauriInputDeviceSelected, tauriOutputDeviceSelected, tauriInputDevices } =
    //       store.getState().scribeRecordModel;
    //     if (!tauriInputDeviceSelected || !tauriOutputDeviceSelected) {
    //       alert('Please select input and output devices');
    //       return;
    //     }
    //     if (!tauriInputDevices?.includes(tauriInputDeviceSelected)) {
    //       alert(
    //         'Please select a valid input device. The current selection may have been disconnected'
    //       );
    //       return;
    //     }
    //     if (!session?.session_id) {
    //       alert('Session ID not found');
    //       return;
    //     }
    //     const isFfmpegInstalled = await recorderFmc.get_ffmpeg_download_status();
    //     if (!isFfmpegInstalled) {
    //       alert('Ffmpeg is installing. Please come back after a few minutes');
    //       return;
    //     }
    //     const { data } = await scribeHttpV2.post<{ recording_id: string }>(
    //       `/sessions/${session.session_id}/restful-segment-transcription`,
    //       {
    //         'axios-retry': {
    //           retries: 3,
    //           retryDelay: (retryCount: number) => {
    //             return retryCount * 1000;
    //           },
    //           onMaxRetryTimesExceeded: () => {
    //             logger.debug("max retries exceeded for session start");
    //           },
    //         },
    //       }
    //     );

    //     logger.debug(data);
    //     dispatch.tauriRecordModel.setChunks({
    //       current_index: 0,
    //       blobs: [],
    //       session_id: session?.session_id,
    //       recording_id: data.recording_id,
    //     });

    //     await recorderFmc.start(
    //       tauriInputDeviceSelected?.name,
    //       tauriOutputDeviceSelected?.name,
    //       session?.session_id
    //     );

    //     logger.debug('start current recording');
    //     // eslint-disable-next-line @typescript-eslint/no-misused-promises
    //     const interval = setInterval(() => {
    //       logger.debug('getting current recording');
    //       const recording = dispatch.tauriRecordModel.getChunkRecordingAndUpload({
    //         sessionId: session?.session_id || '',
    //       });
    //       logger.debug('recording', recording);
    //     }, 30000);

    //     dispatch.tauriRecordModel.setInterval(interval);
    //     dispatch.timerModel.onStart(session?.duration || 0);
    //   } catch (error) {
    //     alert(
    //       'Failed to start recording. Please try again. If the issue persists, please contact support.'
    //     );
    //   }
    // },

    // async getChunkRecordingAndUpload({ sessionId }: { sessionId: string }) {
    //   const { chunks } = store.getState().tauriRecordModel;
    //   const index = chunks.current_index === 0 ? 1 : chunks.current_index + 1;
    //   try {
    //     const recording = await recorderFmc.get_current_recording();
    //     if (!recording) {
    //       return;
    //     }
    //     const byteArray = new Uint8Array(recording?.bytes);
    //     const blob = new Blob([byteArray], { type: 'audio/mp3' });
    //     dispatch.tauriRecordModel.setChunks({
    //       current_index: index,
    //       // don't need to send chunk for now to save memory on the client side.
    //       // TODO: get accurate duration from tauri
    //       blobs: [
    //         ...chunks.blobs,
    //         {
    //           chunk: [],
    //           status: UploadStatus.Queued,
    //           duration: 30,
    //           size: recording.size,
    //           name: recording.name,
    //         },
    //       ],
    //       session_id: sessionId,
    //       recording_id: chunks.recording_id,
    //     });
    //     const recordingId = chunks.recording_id;
    //     if (!recordingId) {
    //       alert('Recording ID not found. Please stop recording and start again');
    //       return;
    //     }
    //     if (blob) {
    //       await dispatch.tauriRecordModel.uploadchunks({
    //         sessionId,
    //         recordingId: recordingId,
    //         index: chunks.current_index,
    //         chunk: blob,
    //         duration: 30,
    //         path: recording.path,
    //       });
    //     }
    //   } catch (error) {
    //     dispatch.tauriRecordModel.editChunkStatus({
    //       index,
    //       status: UploadStatus.Failed,
    //     });
    //     logger.error('error getting chunk recording and upload', error);
    //   }
    // },

    // async uploadchunks(input: {
    //   sessionId: string;
    //   recordingId: string;
    //   index: number;
    //   chunk: Blob;
    //   duration: number;
    //   path?: string;
    // }) {
    //   const { sessionId, recordingId, index, chunk, duration, path } = input;
    //   const formData = new FormData();
    //   formData.append('file', chunk);
    //   formData.append('index', String(index));
    //   formData.append('duration', String(duration));
    //   logger.debug('uploading chunk', index);
    //   const { data } = await scribeHttpV2.post<{ is_success: boolean }>(
    //     `/sessions/${sessionId}/restful-segment-transcription/${recordingId}:transcribe`,
    //     formData,
    //     {
    //       'axios-retry': {
    //         retries: 99999999999,
    //         retryDelay: (retryCount: number) => {
    //           return retryCount * 1000;
    //         },
    //         validateResponse: (response: { data: { is_success: boolean } }) => {
    //           return response.data.is_success;
    //         },
    //         onMaxRetryTimesExceeded: () => {
    //           dispatch.tauriRecordModel.editChunkStatus({
    //             index,
    //             status: UploadStatus.Failed,
    //           });
    //           if (path) {
    //             void recorderFmc.remove_file_from_path(path);
    //           }
    //         },
    //       },
    //     }
    //   );

    //   if (data.is_success) {
    //     dispatch.tauriRecordModel.editChunkStatus({
    //       index,
    //       status: UploadStatus.Completed,
    //     });
    //     if (path) {
    //       void recorderFmc.remove_file_from_path(path);
    //     }
    //   } else {
    //     dispatch.tauriRecordModel.editChunkStatus({
    //       index,
    //       status: UploadStatus.Failed,
    //     });
    //   }
    // },

    async get_backup_file() {
      const recording = await recorderFmc.get_backup_file();
      if (!recording) {
        return;
      }
      const bytes = await recorderFmc.get_file_from_path(recording);
      const byteArray = new Uint8Array(bytes.bytes);
      const blob = new Blob([byteArray], { type: 'audio/mp3' });
      return { blob, path: recording };
    },

    async stopTauriRecording() {
      try {
        if (!store.getState().timerModel.isRunning) {
          await waitUntil(() => {
            return store.getState().timerModel.isRunning;
          }, 2000);
        }
        dispatch.timerModel.onPause();
        await recorderFmc.stop();

        // DO NOT emit recording-stopped event here!
        // The idle stream restart should happen AFTER all recording cleanup operations
        // (chunk upload, backup file retrieval, etc.) complete in useMutateStopRecording.
        // Emitting here causes a race condition where the idle stream restarts while
        // concurrent operations are still accessing audio resources.
        // See useMutateStopRecording.ts for where recordingLifecycle.emitStopped() is called.
        logger.debug('[Recording] recorderFmc.stop() completed');
      } catch (error) {
        logger.error('error stopping tauri recording', error);
        throw error;
      } finally {
        // The 60s upload interval must die even when the recorder stop throws.
        const interval = store.getState().tauriRecordModel.interval;
        if (interval) {
          clearInterval(interval);
        }
      }
    },

    async getFilesForSession(sessionId: string) {
      const files = await recorderFmc.get_files_for_session(sessionId);
      dispatch.scribeRecordModel.setTauriSessionFiles({
        sessionId: sessionId,
        files: files,
      });
    },
  }),
});
